import React, { useState, useRef, useEffect, useId } from 'react';
import { useLabRange, type LabRangeType } from '../lib/labRangeContext';

/** Segmented toggle for Standard / Optimal lab reference ranges with info tooltip. */
export default function LabRangeToggle({ compact }: { compact?: boolean }) {
  const { rangeType, setRangeType } = useLabRange();
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  // Close tooltip on outside tap (mobile)
  useEffect(() => {
    if (!showInfo) return;
    function handleClick(e: MouseEvent) {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) setShowInfo(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showInfo]);

  const options: { key: LabRangeType; label: string }[] = [
    { key: 'optimal', label: 'Optimal' },
    { key: 'standard', label: 'Standard' },
  ];

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-0.5 gap-0.5">
        {options.map(o => (
          <button
            key={o.key}
            onClick={() => setRangeType(o.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              rangeType === o.key
                ? 'bg-white dark:bg-gray-700 text-arete-forest dark:text-gray-100 shadow-sm'
                : 'text-arete-muted hover:text-arete-forest dark:hover:text-gray-200'
            }`}
          >
            {compact ? o.label : `${o.label} Range`}
          </button>
        ))}
      </div>

      {/* Info icon with tooltip */}
      <div className="relative" ref={infoRef}>
        <button
          onClick={() => setShowInfo(v => !v)}
          onMouseEnter={() => setShowInfo(true)}
          onMouseLeave={() => setShowInfo(false)}
          className="flex items-center justify-center flex-shrink-0 -m-2.5 p-2.5"
          aria-label="Learn about range types"
          aria-describedby={showInfo ? tooltipId : undefined}
        >
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center text-xs text-arete-muted hover:text-arete-forest transition-colors"
            style={{ background: 'rgba(100,116,139,0.08)' }}
          >
            ⓘ
          </span>
        </button>
        {showInfo && (
          <div
            id={tooltipId}
            role="tooltip"
            className="absolute right-0 top-7 z-50 w-64 rounded-xl px-3.5 py-3 text-xs leading-relaxed shadow-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-arete-forest dark:text-gray-100"
          >
            <p className="font-semibold mb-1.5">Range Types</p>
            <p><span className="font-medium">Standard:</span> Conventional lab reference ranges used by most labs.</p>
            <p className="mt-1"><span className="font-medium">Optimal:</span> Evidence-based functional ranges for wellness — tighter thresholds linked to mental health outcomes.</p>
          </div>
        )}
      </div>
    </div>
  );
}
